import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../utils/api';

/*
 * When a dish was started and how it is kept (plate_culture.inoculated_at and
 * incubation_light, migration 0111; docs/LIVE_UX_PLAN.md L5), for every form that
 * starts a plate. Both may stay empty: the server takes its own clock for a dish
 * started now, and light stays not said.
 *
 * value: {startedAt (a datetime-local string), light}; onChange(patch).
 * startBody(value) is what those forms add to their POST or PUT.
 *
 * PlateStartEditor is the plate detail's: shows them, and corrects them with
 * PUT /plates/{id}, where an empty box clears what was there.
 */
export const LIGHTS = Object.freeze(['dark', 'ambient', 'photoperiod', 'continuous']);
export const EMPTY_START = Object.freeze({ startedAt: '', light: '' });

export function startBody(value) {
  return {
    ...(value?.startedAt ? { inoculated_at: new Date(value.startedAt).toISOString() } : {}),
    ...(value?.light ? { incubation_light: value.light } : {}),
  };
}

const toLocalInput = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};

export default function PlateStartFields({ value = EMPTY_START, onChange, testIdPrefix = 'plate-start',
                                           labelStyle = fieldStyle, inputStyle = boxStyle, help = true }) {
  const { t } = useTranslation('cultureAnalysis');
  const id = useId();
  return (
    <>
      <div style={labelStyle}>
        <label htmlFor={`${id}-at`}>{t('plateStart.startedAt')}</label>
        <input id={`${id}-at`} type="datetime-local" value={value.startedAt} style={inputStyle}
               max={toLocalInput(new Date().toISOString())} data-testid={`${testIdPrefix}-at`}
               onChange={e => onChange({ startedAt: e.target.value })} />
        {help && <small style={helpStyle}>{t('plateStart.startedHelp')}</small>}
      </div>
      <div style={labelStyle}>
        <label htmlFor={`${id}-light`}>{t('plateStart.light')}</label>
        <select id={`${id}-light`} value={value.light} style={inputStyle} data-testid={`${testIdPrefix}-light`}
                onChange={e => onChange({ light: e.target.value })}>
          <option value="">{t('plateStart.lightNone')}</option>
          {LIGHTS.map(light => <option key={light} value={light}>{t(`plateStart.lights.${light}`)}</option>)}
        </select>
      </div>
    </>
  );
}

export function PlateStartEditor({ plate, canEdit = false, onSaved = () => {} }) {
  const { t, i18n } = useTranslation('cultureAnalysis');
  const [draft, setDraft] = useState(null);
  const [state, setState] = useState({ saving: false, failed: '' });
  const started = plate?.inoculated_at;
  const dateText = (iso) => new Date(iso).toLocaleString(i18n.language);
  const days = plate?.incubation_days;
  const save = async () => {
    setState({ saving: true, failed: '' });
    // Only what changed goes: a start this form never touched keeps its seconds, and its audit row stays quiet.
    const body = {};
    if (draft.startedAt !== toLocalInput(started)) {
      body.inoculated_at = draft.startedAt ? new Date(draft.startedAt).toISOString() : null;
    }
    if (draft.light !== (plate.incubation_light || '')) body.incubation_light = draft.light || null;
    if (!Object.keys(body).length) {
      setDraft(null);
      setState({ saving: false, failed: '' });
      return;
    }
    try {
      await api.put(`/plates/${plate.plate_id}`, body);
      setDraft(null);
      setState({ saving: false, failed: '' });
      const moved = 'inoculated_at' in body ? {
        incubation_days: body.inoculated_at ? (Date.now() - new Date(body.inoculated_at).getTime()) / 86400000 : null,
        incubation_basis: body.inoculated_at ? 'inoculated_at' : null,
      } : {};
      onSaved({ ...body, ...moved });
    } catch (err) {
      const answer = err?.response?.data || {};
      setState({ saving: false,
                 failed: answer.code ? t(`errors.${answer.code}`, answer.error || '') : (answer.error || t('plateStart.saveFailed')) });
    }
  };
  if (draft) {
    return (
      <div data-testid="plate-start-editor" style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <PlateStartFields value={draft} onChange={patch => setDraft(d => ({ ...d, ...patch }))} help={false}
                          testIdPrefix="plate-start-edit" />
        <small style={helpStyle}>{t('plateStart.editHelp')}</small>
        {state.failed && <p role="alert" style={errorStyle}>{state.failed}</p>}
        <div style={{ display: 'flex', gap: 8 }}>
          <button type="button" onClick={save} disabled={state.saving} data-testid="plate-start-save" style={linkStyle}>
            {t('plateStart.save')}
          </button>
          <button type="button" onClick={() => setDraft(null)} disabled={state.saving} style={linkStyle}>
            {t('plateStart.cancel')}
          </button>
        </div>
      </div>
    );
  }
  return (
    <div data-testid="plate-start-shown" style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'baseline' }}>
      <span>
        {started ? t('plateStart.startedOn', { date: dateText(started) }) : t('plateStart.notRecorded')}
        {days !== null && days !== undefined && (
          <> · {t(plate.incubation_basis === 'inoculated_at' ? 'plateStart.incubating' : 'plateStart.incubatingSinceMade',
                  { days: days.toFixed(1) })}</>
        )}
        {plate?.incubation_light && <> · {t(`plateStart.lights.${plate.incubation_light}`)}</>}
      </span>
      {canEdit && plate?.status !== 'awaiting_creation' && (
        <button type="button" data-testid="plate-start-edit" style={linkStyle}
                onClick={() => setDraft({ startedAt: toLocalInput(started), light: plate.incubation_light || '' })}>
          {t('plateStart.edit')}
        </button>
      )}
    </div>
  );
}

const fieldStyle = { display: 'flex', flexDirection: 'column', gap: 3, fontSize: 13 };
const boxStyle = { padding: '6px 8px', border: '1px solid var(--c-border-strong)', borderRadius: 6, background: 'var(--c-surface)', color: 'var(--c-text-fg)', fontSize: 13 };
const helpStyle = { fontSize: 12, color: 'var(--c-text-muted-fg)' };
const errorStyle = { margin: 0, fontSize: 13, color: 'var(--c-danger-fg)' };
const linkStyle = { background: 'none', border: 'none', cursor: 'pointer', color: 'var(--c-primary)', padding: 0, fontSize: 13 };
